// Joining a class as more than one page shows it (RFC-043 M3, byj-1wv.4.1,
// byj-1wv.51.20): the policy in plain words, the consent text and its
// version, the commands that share to the class, the consent screen
// (JoinView) and the state of reading and agreeing (useJoin) — for /c/join,
// /classes' join, the instructor's preview of a student's view and the
// student's class page, so none of them imports another page.
import { useCallback, useState } from "react";
import { api, ApiError, type Assignment, type ClassPolicy, type Consent, type JoinPreview } from "../api";
import { t, when } from "../i18n";
import { CommandLine, shareCommand } from "./CommandLine";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A class's policy in plain words, for the person deciding to join. */
export function PolicyWords({ policy }: { policy: ClassPolicy }) {
  return (
    <ul className="policy-words">
      {policy.inject ? (
        <li>
          {t("classes:policyMayInject")} {policy.consent_inject ? t("classes:policyMayInjectConsent") : t("classes:policyMayInjectAsk")}
        </li>
      ) : (
        <li>{t("classes:policyNoInject")}</li>
      )}
      <li>{t("classes:policyRetention", { count: policy.retention_days })}</li>
    </ul>
  );
}

/** The command that shares a session with the class, or to one of its assignments (RFC-043 model 19). */
export function ShareCommand({ code, assignment }: { code: string; assignment?: string }) {
  return <CommandLine command={shareCommand(code, assignment)} />;
}

/** An assignment's title, key and due date, on one line. */
function AssignmentHead({ a }: { a: Assignment }) {
  return (
    <div className="assignment-head">
      <strong>{a.title}</strong> <code>{a.key}</code>
      {a.due_at && <span className="muted small"> · {t("classes:dueOn", { at: when(a.due_at) })}</span>}
    </div>
  );
}

/** The class's open assignments, each a row with what to do and its share command. */
export function AssignmentCommands({ code, assignments }: { code: string; assignments: Assignment[] }) {
  if (assignments.length === 0) return null;
  return (
    <section data-testid="assignment-commands">
      <h3 className="rows-head">
        {t("classes:assignments")} · {assignments.length}
      </h3>
      <ul className="rows">
        {assignments.map((a) => (
          <li key={a.key} className="rows-item stack">
            <AssignmentHead a={a} />
            {a.text && <p className="small flush pre-wrap">{a.text}</p>}
            <ShareCommand code={code} assignment={a.key} />
          </li>
        ))}
      </ul>
      <p className="muted small">{t("classes:shareWithoutAssignment")}</p>
    </section>
  );
}

/** The consent text, under its title and version: what joining agrees to, the join page's and the class page's alike. */
export function ConsentText({ consent, stale = false }: { consent: Consent; stale?: boolean }) {
  return (
    <>
      <h3>
        {t("classes:consentTitle")} <span className="muted small">{t("classes:consentVersion", { v: consent.version })}</span>
      </h3>
      {stale && <p className="warn">{t("classes:staleConsent")}</p>}
      <div className="consent-text" data-testid="consent-text">
        {consent.text}
      </div>
    </>
  );
}

/**
 * What joining means, and the button that agrees: the class, the policy, its
 * open assignments, the consent text and its version. `onAgree` absent, the
 * button is drawn and does nothing (the instructor's preview).
 */
export function JoinView({ preview, stale = false, busy = false, onAgree, titleId }: {
  preview: JoinPreview;
  stale?: boolean;
  busy?: boolean;
  onAgree?: () => void;
  /** its heading's id, for a dialog it is drawn in to be named by */
  titleId?: string;
}) {
  const assignments = preview.assignments ?? [];
  return (
    <>
      {/* in a dialog, what the focus goes to as this step opens, so Tab stays in it */}
      <h1 id={titleId} tabIndex={titleId ? -1 : undefined}>
        {t("classes:joinTitle", { name: preview.class.name })}
      </h1>
      <p>{t("classes:joinLead")}</p>
      <section>
        <h3>{t("classes:policyTitle")}</h3>
        <PolicyWords policy={preview.policy} />
      </section>
      {assignments.length > 0 && (
        <section>
          <h3>{t("classes:assignments")}</h3>
          <ul className="rows">
            {assignments.map((a) => (
              <li key={a.key} className="rows-item">
                <AssignmentHead a={a} />
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <ConsentText consent={preview.consent} stale={stale} />
      </section>
      <button className="primary" disabled={busy || !onAgree} onClick={onAgree}>
        {busy ? t("classes:joining") : t("classes:agreeAndJoin")}
      </button>
    </>
  );
}

/**
 * Joining a class by its code (RFC-043 M3), the join page's and the classes
 * page's dialog's alike: `look` asks what joining it means; `agree` joins
 * with the consent version that was read — a consent changed meanwhile is
 * read again and said so (`stale`), to be agreed to anew. `error` is said as
 * it is: a code no class has, or what went wrong.
 */
export function useJoin() {
  const [read, setRead] = useState<{ code: string; preview: JoinPreview } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);
  const fetchPreview = useCallback(async (code: string) => {
    try {
      setRead({ code, preview: await api.joinPreview(code) });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 404 ? t("classes:badCode") : t("common:error", { m: errText(err) }));
    }
  }, []);
  const look = useCallback(
    async (code: string) => {
      setError(null);
      setStale(false);
      setBusy(true);
      await fetchPreview(code);
      setBusy(false);
    },
    [fetchPreview],
  );
  const agree = useCallback(async (): Promise<{ id: string; name: string } | null> => {
    if (!read) return null;
    setBusy(true);
    setError(null);
    setStale(false);
    try {
      return (await api.joinClass(read.code, read.preview.consent.version)).class;
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // the consent changed under the reader: the new text, asked again
        setStale(true);
        await fetchPreview(read.code);
      } else setError(t("common:error", { m: errText(err) }));
      return null;
    } finally {
      setBusy(false);
    }
  }, [read, fetchPreview]);
  /** Back to before the code was looked at. */
  const forget = useCallback(() => {
    setRead(null);
    setError(null);
    setStale(false);
  }, []);
  return { preview: read?.preview ?? null, error, stale, busy, look, agree, forget, setError };
}
